import { useParams } from 'react-router-dom'
import * as React from 'react'
import { useFormAlertReminders } from './useFormAlertReminders'
import { useMutationAlertRemindersConfiguration } from './useMutationAlertRemindersConfiguration'
import { useQueryAlertRemindersConfiguration } from './useQueryAlertRemindersConfiguration'

import { FieldsetSelect } from '../../components/FieldsetSelect'
import { durationOptionsValues } from './helpers'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { SettingsPageWrapper } from '../../components/SettingsPageWrapper'
import { formatDropdownTimespan } from '@/app/common/dateHelper'
import { SettingsSectionWithTabs } from '../../components/SettingsSectionWithTabs'

// Alert reminders configuration page
export const AlertRemindersPageContent: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>()
  const { t } = useLocalize()
  const mutation = useMutationAlertRemindersConfiguration(companyId)
  const {
    data: queryData,
    isFetching,
    error,
    isError,
  } = useQueryAlertRemindersConfiguration(companyId)
  const { draft, handleSelect, isDirty, handleReset } =
    useFormAlertReminders(queryData)

  const handleSave = () => mutation.mutate(draft)

  const diffLabels = React.useMemo<Record<string, string>>(() => {
    if (!draft) return {}
    return Object.fromEntries(
      Object.keys(draft).map((name) => [name, t(`configuration.alertReminders.${name}`)])
    )
  }, [draft, t])

  const diffFormatters = React.useMemo<Record<string, (v: unknown) => string>>(() => {
    if (!draft) return {}
    return Object.fromEntries(
      Object.keys(draft).map((name) => [name, (v: unknown) => formatDropdownTimespan(String(v), t)])
    )
  }, [draft, t])

  return (
    <SettingsPageWrapper
      isReady={!!draft}
      isPending={isFetching || mutation.isPending}
      isError={isError}
      handleSave={handleSave}
      handleReset={handleReset}
      isDirty={isDirty}
      errorMsg={t('configuration-alertReminders.load-error')}
      globalErrorMsg={t('configuration-alertReminders.load-error-global')}
      diffBefore={(queryData ?? {}) as Record<string, unknown>}
      diffAfter={(draft ?? {}) as Record<string, unknown>}
      diffLabels={diffLabels}
      diffFormatters={diffFormatters}
    >
      <SettingsSectionWithTabs>
        <div
          data-tab-name="a"
          title={t('configuration-alertReminders.tab-settings')}
          className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
        >
          <form className="tw-flex tw-flex-col tw-gap-4">
            {Object.keys(draft).map((name) => (
              <FieldsetSelect
                label={t(`configuration.alertReminders.${name}`)}
                key={name}
                name={name}
                placeholder="Select..."
                options={durationOptionsValues.map((v) => ({
                  value: v,
                  label: formatDropdownTimespan(v, t),
                }))}
                value={draft[name]}
                onChange={handleSelect}
                valueClassName="tw-max-w-sm"
              />
            ))}
          </form>
        </div>
      </SettingsSectionWithTabs>
    </SettingsPageWrapper>
  )
}
